/**
 * CronInput Component
 *
 * A user-friendly GUI for creating and editing cron expressions.
 * Supports all standard cron features with an intuitive interface.
 *
 * Features:
 * - Frequency presets (Weekly, Daily, Monthly, Yearly, Custom)
 * - Time selection (hour, minute)
 * - Day of week selection for weekly schedules
 * - Day of month selection for monthly schedules
 * - Month selection for yearly schedules
 * - Advanced mode for raw cron expression editing
 * - Real-time validation and human-readable preview
 */

import { useMemo, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import {
  CalendarIcon,
  ClockIcon,
  CaretDownIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';

export type CronFrequency =
  | 'every_n_minutes'
  | 'hourly'
  | 'daily'
  | 'weekly'
  | 'monthly'
  | 'yearly'
  | 'custom';

export interface CronInputProps {
  /** Current cron expression value */
  value: string;
  /** Called when the cron expression changes */
  onChange: (value: string) => void;
  /** Label for the input */
  label?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Additional CSS class names */
  className?: string;
  /** Default frequency to show (defaults to 'weekly') */
  defaultFrequency?: CronFrequency;
  /** Show advanced mode toggle */
  showAdvanced?: boolean;
}

const DAYS_OF_WEEK = [
  { value: '0', label: 'Sun', fullLabel: 'Sunday' },
  { value: '1', label: 'Mon', fullLabel: 'Monday' },
  { value: '2', label: 'Tue', fullLabel: 'Tuesday' },
  { value: '3', label: 'Wed', fullLabel: 'Wednesday' },
  { value: '4', label: 'Thu', fullLabel: 'Thursday' },
  { value: '5', label: 'Fri', fullLabel: 'Friday' },
  { value: '6', label: 'Sat', fullLabel: 'Saturday' },
];

const MONTHS = [
  { value: '1', label: 'Jan', fullLabel: 'January' },
  { value: '2', label: 'Feb', fullLabel: 'February' },
  { value: '3', label: 'Mar', fullLabel: 'March' },
  { value: '4', label: 'Apr', fullLabel: 'April' },
  { value: '5', label: 'May', fullLabel: 'May' },
  { value: '6', label: 'Jun', fullLabel: 'June' },
  { value: '7', label: 'Jul', fullLabel: 'July' },
  { value: '8', label: 'Aug', fullLabel: 'August' },
  { value: '9', label: 'Sep', fullLabel: 'September' },
  { value: '10', label: 'Oct', fullLabel: 'October' },
  { value: '11', label: 'Nov', fullLabel: 'November' },
  { value: '12', label: 'Dec', fullLabel: 'December' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: String(i),
  label:
    i === 0
      ? '12 AM'
      : i < 12
        ? `${i} AM`
        : i === 12
          ? '12 PM'
          : `${i - 12} PM`,
}));

const MINUTES = Array.from({ length: 12 }, (_, i) => ({
  value: String(i * 5),
  label: String(i * 5).padStart(2, '0'),
}));

const DAYS_OF_MONTH = Array.from({ length: 31 }, (_, i) => ({
  value: String(i + 1),
  label: String(i + 1),
}));

// Minute interval options for "every N minutes"
const MINUTE_INTERVALS = [
  { value: '1', label: 'Every minute' },
  { value: '5', label: 'Every 5 minutes' },
  { value: '10', label: 'Every 10 minutes' },
  { value: '15', label: 'Every 15 minutes' },
  { value: '20', label: 'Every 20 minutes' },
  { value: '30', label: 'Every 30 minutes' },
];

/**
 * Parse a cron expression into its components
 */
export function parseCronExpression(cron: string): {
  minute: string;
  hour: string;
  dayOfMonth: string;
  month: string;
  dayOfWeek: string;
  isValid: boolean;
} {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) {
    return {
      minute: '0',
      hour: '9',
      dayOfMonth: '*',
      month: '*',
      dayOfWeek: '*',
      isValid: false,
    };
  }

  return {
    minute: parts[0],
    hour: parts[1],
    dayOfMonth: parts[2],
    month: parts[3],
    dayOfWeek: parts[4],
    isValid: true,
  };
}

/**
 * Build a cron expression from components
 */
export function buildCronExpression(
  minute: string,
  hour: string,
  dayOfMonth: string,
  month: string,
  dayOfWeek: string,
): string {
  return `${minute} ${hour} ${dayOfMonth} ${month} ${dayOfWeek}`;
}

/**
 * Detect the frequency type from a cron expression
 */
export function detectFrequency(cron: string): CronFrequency {
  const { minute, hour, dayOfMonth, month, dayOfWeek, isValid } =
    parseCronExpression(cron);

  if (!isValid) return 'custom';

  // Every N minutes: minute contains step (*/N or 0,5,10...), hour is *, all others are *
  if (
    hour === '*' &&
    dayOfMonth === '*' &&
    month === '*' &&
    dayOfWeek === '*'
  ) {
    // Check for minute step patterns like */5 or */15
    if (minute.startsWith('*/') || minute === '*') {
      return 'every_n_minutes';
    }
  }

  // Hourly: minute is specific (not step), hour is *, all others are *
  if (
    !minute.includes('/') &&
    !minute.includes(',') &&
    hour === '*' &&
    dayOfMonth === '*' &&
    month === '*' &&
    dayOfWeek === '*'
  ) {
    return 'hourly';
  }

  // Weekly: day of month is *, month is *, day of week is specific
  if (dayOfMonth === '*' && month === '*' && dayOfWeek !== '*') {
    return 'weekly';
  }

  // Daily: day of month is *, month is *, day of week is *, hour is specific
  if (
    hour !== '*' &&
    dayOfMonth === '*' &&
    month === '*' &&
    dayOfWeek === '*'
  ) {
    return 'daily';
  }

  // Monthly: day of month is specific, month is *, day of week is *
  if (dayOfMonth !== '*' && month === '*' && dayOfWeek === '*') {
    return 'monthly';
  }

  // Yearly: month is specific
  if (month !== '*') {
    return 'yearly';
  }

  return 'custom';
}

/**
 * Generate a human-readable description of a cron expression
 */
export function describeCronExpression(cron: string): string {
  const { minute, hour, dayOfMonth, month, dayOfWeek, isValid } =
    parseCronExpression(cron);

  if (!isValid) return 'Invalid cron expression';

  const timeStr = formatTime(hour, minute);
  const frequency = detectFrequency(cron);

  switch (frequency) {
    case 'every_n_minutes': {
      // Parse step from minute field (e.g., */5 -> 5)
      if (minute === '*') return 'Every minute';
      const stepMatch = minute.match(/^\*\/(\d+)$/);
      if (stepMatch) {
        const interval = parseInt(stepMatch[1]);
        if (interval === 1) return 'Every minute';
        return `Every ${interval} minutes`;
      }
      return `Custom minute schedule: ${cron}`;
    }

    case 'hourly': {
      const m = parseInt(minute);
      if (m === 0) return 'Every hour on the hour';
      return `Every hour at :${String(m).padStart(2, '0')}`;
    }

    case 'daily':
      return `Every day at ${timeStr}`;

    case 'weekly': {
      const days = parseDaysOfWeek(dayOfWeek);
      if (days.length === 0) return `Invalid weekly schedule`;
      if (days.length === 7) return `Every day at ${timeStr}`;
      const dayNames = days.map(
        (d) => DAYS_OF_WEEK.find((dow) => dow.value === d)?.fullLabel || d,
      );
      return `Every ${formatList(dayNames)} at ${timeStr}`;
    }

    case 'monthly': {
      const days = parseDaysOfMonth(dayOfMonth);
      if (days.length === 0) return `Invalid monthly schedule`;
      const dayStr =
        days.length === 1
          ? ordinal(parseInt(days[0]))
          : formatList(days.map((d) => ordinal(parseInt(d))));
      return `On the ${dayStr} of every month at ${timeStr}`;
    }

    case 'yearly': {
      const months = parseMonths(month);
      const days = parseDaysOfMonth(dayOfMonth);
      if (months.length === 0) return `Invalid yearly schedule`;
      const monthNames = months.map(
        (m) => MONTHS.find((mo) => mo.value === m)?.fullLabel || m,
      );
      const dayStr = days.length === 1 ? ordinal(parseInt(days[0])) : days[0];
      return `On ${monthNames[0]} ${dayStr} at ${timeStr}`;
    }

    default:
      return `Custom schedule: ${cron}`;
  }
}

function formatTime(hour: string, minute: string): string {
  const h = parseInt(hour);
  const m = parseInt(minute);
  if (isNaN(h) || isNaN(m)) return `${hour}:${minute}`;
  const period = h >= 12 ? 'PM' : 'AM';
  const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${displayHour}:${String(m).padStart(2, '0')} ${period}`;
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function formatList(items: string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

function parseDaysOfWeek(expr: string): string[] {
  if (expr === '*') return DAYS_OF_WEEK.map((d) => d.value);
  return parseListExpression(expr, 0, 6);
}

function parseDaysOfMonth(expr: string): string[] {
  if (expr === '*') return ['*'];
  return parseListExpression(expr, 1, 31);
}

function parseMonths(expr: string): string[] {
  if (expr === '*') return MONTHS.map((m) => m.value);
  return parseListExpression(expr, 1, 12);
}

function parseListExpression(expr: string, min: number, max: number): string[] {
  const result: string[] = [];
  const parts = expr.split(',');

  for (const part of parts) {
    // Check for step operator first (handles both */2 and 1-5/2)
    if (part.includes('/')) {
      const [base, step] = part.split('/');
      const stepVal = parseInt(step);
      if (isNaN(stepVal) || stepVal <= 0) continue;

      // Base can be: *, single number, or range (1-5)
      let rangeStart: number;
      let rangeEnd: number = max;

      if (base === '*') {
        rangeStart = min;
      } else if (base.includes('-')) {
        // Range with step: 1-5/2 means 1,3,5
        const [rangeStartStr, rangeEndStr] = base.split('-');
        rangeStart = parseInt(rangeStartStr);
        rangeEnd = parseInt(rangeEndStr);
        if (isNaN(rangeStart) || isNaN(rangeEnd)) continue;
      } else {
        // Single value with step: 1/2 means start at 1, step by 2
        rangeStart = parseInt(base);
        if (isNaN(rangeStart)) continue;
      }

      for (let i = rangeStart; i <= rangeEnd && i <= max; i += stepVal) {
        if (i >= min) result.push(String(i));
      }
    } else if (part.includes('-')) {
      // Range without step: 1-5
      const [start, end] = part.split('-').map((n) => parseInt(n));
      if (!isNaN(start) && !isNaN(end)) {
        for (let i = start; i <= end && i <= max; i++) {
          if (i >= min) result.push(String(i));
        }
      }
    } else {
      // Single value
      const val = parseInt(part);
      if (!isNaN(val) && val >= min && val <= max) {
        result.push(String(val));
      }
    }
  }

  return [...new Set(result)].sort((a, b) => parseInt(a) - parseInt(b));
}

/**
 * Validate a cron expression
 */
export function validateCronExpression(cron: string): {
  isValid: boolean;
  error?: string;
} {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) {
    return {
      isValid: false,
      error: `Expected 5 fields, got ${parts.length}`,
    };
  }

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  // Validate each field
  const validators: Array<{
    field: string;
    value: string;
    min: number;
    max: number;
  }> = [
    { field: 'Minute', value: minute, min: 0, max: 59 },
    { field: 'Hour', value: hour, min: 0, max: 23 },
    { field: 'Day of month', value: dayOfMonth, min: 1, max: 31 },
    { field: 'Month', value: month, min: 1, max: 12 },
    { field: 'Day of week', value: dayOfWeek, min: 0, max: 6 },
  ];

  for (const { field, value, min, max } of validators) {
    if (!isValidCronField(value, min, max)) {
      return {
        isValid: false,
        error: `Invalid ${field.toLowerCase()} field: ${value}`,
      };
    }
  }

  return { isValid: true };
}

function isValidCronField(value: string, min: number, max: number): boolean {
  if (value === '*') return true;

  const parts = value.split(',');
  for (const part of parts) {
    // Check for step operator first (handles both */2 and 1-5/2)
    if (part.includes('/')) {
      const [base, step] = part.split('/');
      if (!isValidNumber(step, 1, max)) return false;

      // Base can be: *, single number, or range (1-5)
      if (base === '*') {
        // Valid: */2
      } else if (base.includes('-')) {
        // Validate range: 1-5/2
        const [start, end] = base.split('-');
        if (!isValidNumber(start, min, max) || !isValidNumber(end, min, max))
          return false;
      } else {
        // Validate single number: 1/2
        if (!isValidNumber(base, min, max)) return false;
      }
    } else if (part.includes('-')) {
      // Range without step: 1-5
      const [start, end] = part.split('-');
      if (!isValidNumber(start, min, max) || !isValidNumber(end, min, max))
        return false;
    } else {
      // Single value
      if (!isValidNumber(part, min, max)) return false;
    }
  }

  return true;
}

function isValidNumber(value: string, min: number, max: number): boolean {
  const num = parseInt(value);
  return !isNaN(num) && num >= min && num <= max;
}

export function CronInput({
  value,
  onChange,
  label,
  disabled = false,
  className,
  // NOTE: currently unused (kept via '_' prefix) — revisit and delete if not needed
  defaultFrequency: _defaultFrequency = 'weekly',
  showAdvanced = true,
}: CronInputProps) {
  const gt = useGT();
  const [isAdvanced, setIsAdvanced] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const parsed = useMemo(() => parseCronExpression(value), [value]);
  const frequency = useMemo(() => detectFrequency(value), [value]);
  const validation = useMemo(() => validateCronExpression(value), [value]);
  const description = useMemo(() => describeCronExpression(value), [value]);

  const selectedDaysOfWeek = useMemo(
    () => parseDaysOfWeek(parsed.dayOfWeek),
    [parsed.dayOfWeek],
  );
  const selectedDayOfMonth = useMemo(
    () => (parsed.dayOfMonth === '*' ? '1' : parsed.dayOfMonth.split(',')[0]),
    [parsed.dayOfMonth],
  );
  const selectedMonth = useMemo(
    () => (parsed.month === '*' ? '1' : parsed.month.split(',')[0]),
    [parsed.month],
  );

  // Get current minute interval for every_n_minutes frequency
  const selectedMinuteInterval = useMemo(() => {
    if (parsed.minute === '*') return '1';
    const match = parsed.minute.match(/^\*\/(\d+)$/);
    return match ? match[1] : '5';
  }, [parsed.minute]);

  const handleFrequencyChange = (newFrequency: CronFrequency) => {
    const { minute, hour } = parsed;

    switch (newFrequency) {
      case 'every_n_minutes':
        // Default to every 5 minutes
        onChange(buildCronExpression('*/5', '*', '*', '*', '*'));
        break;
      case 'hourly':
        // Default to on the hour
        onChange(buildCronExpression('0', '*', '*', '*', '*'));
        break;
      case 'daily':
        onChange(buildCronExpression(minute, hour, '*', '*', '*'));
        break;
      case 'weekly':
        // Default to Friday
        onChange(buildCronExpression(minute, hour, '*', '*', '5'));
        break;
      case 'monthly':
        onChange(buildCronExpression(minute, hour, '1', '*', '*'));
        break;
      case 'yearly':
        onChange(buildCronExpression(minute, hour, '1', '1', '*'));
        break;
      case 'custom':
        break;
    }
  };

  const handleTimeChange = (hour: string, minute: string) => {
    onChange(
      buildCronExpression(
        minute,
        hour,
        parsed.dayOfMonth,
        parsed.month,
        parsed.dayOfWeek,
      ),
    );
  };

  const handleDayOfWeekToggle = (day: string) => {
    const current = new Set(selectedDaysOfWeek);
    if (current.has(day)) {
      current.delete(day);
    } else {
      current.add(day);
    }

    // Ensure at least one day is selected
    if (current.size === 0) return;

    const days = [...current].sort((a, b) => parseInt(a) - parseInt(b));
    const { minute, hour, month } = parsed;

    onChange(buildCronExpression(minute, hour, '*', month, days.join(',')));
  };

  const handleDayOfMonthChange = (day: string) => {
    const { minute, hour, month } = parsed;
    onChange(buildCronExpression(minute, hour, day, month, '*'));
  };

  const handleMonthChange = (month: string) => {
    const { minute, hour, dayOfMonth } = parsed;
    onChange(buildCronExpression(minute, hour, dayOfMonth, month, '*'));
  };

  const handleMinuteIntervalChange = (interval: string) => {
    if (interval === '1') {
      onChange(buildCronExpression('*', '*', '*', '*', '*'));
    } else {
      onChange(buildCronExpression(`*/${interval}`, '*', '*', '*', '*'));
    }
  };

  const handleHourlyMinuteChange = (minute: string) => {
    onChange(buildCronExpression(minute, '*', '*', '*', '*'));
  };

  const handleRawChange = (rawValue: string) => {
    onChange(rawValue);
  };

  return (
    <div className={cn('space-y-2', className)}>
      {label && <Label className="text-sm font-medium">{label}</Label>}

      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            disabled={disabled}
            className={cn(
              'w-full justify-between text-left font-normal',
              !validation.isValid && 'border-destructive',
            )}
          >
            <div className="flex items-center gap-2 truncate">
              <CalendarIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="truncate">{description}</span>
            </div>
            <CaretDownIcon className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-80 p-0" align="start">
          <div className="p-3 space-y-4">
            {/* Advanced mode toggle */}
            {showAdvanced && (
              <div className="flex items-center justify-between">
                <Label htmlFor="advanced-mode" className="text-sm">
                  <T context="cron builder advanced mode toggle">
                    Advanced mode
                  </T>
                </Label>
                <Switch
                  id="advanced-mode"
                  checked={isAdvanced}
                  onCheckedChange={setIsAdvanced}
                />
              </div>
            )}

            {isAdvanced ? (
              /* Advanced: Raw cron input */
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">
                  <T context="cron builder raw expression label">
                    Cron Expression (minute hour day month weekday)
                  </T>
                </Label>
                <Input
                  value={value}
                  onChange={(e) => handleRawChange(e.target.value)}
                  placeholder={gt('0 9 * * 5', {
                    context: 'cron expression example placeholder',
                  })}
                  className={cn(
                    'font-mono text-sm',
                    !validation.isValid && 'border-destructive',
                  )}
                />
                {!validation.isValid && (
                  <div className="flex items-center gap-1 text-xs text-destructive">
                    <WarningCircleIcon className="h-3 w-3" />
                    {validation.error}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">{description}</p>
              </div>
            ) : (
              /* Simple: GUI mode */
              <>
                {/* Frequency selector */}
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">
                    <T context="cron builder frequency field">Frequency</T>
                  </Label>
                  <Select
                    value={frequency}
                    onValueChange={(v) =>
                      handleFrequencyChange(v as CronFrequency)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="every_n_minutes">
                        <T context="cron builder frequency option">
                          Every N Minutes
                        </T>
                      </SelectItem>
                      <SelectItem value="hourly">
                        <T context="cron builder frequency option">Hourly</T>
                      </SelectItem>
                      <SelectItem value="daily">
                        <T context="cron builder frequency option">Daily</T>
                      </SelectItem>
                      <SelectItem value="weekly">
                        <T context="cron builder frequency option">Weekly</T>
                      </SelectItem>
                      <SelectItem value="monthly">
                        <T context="cron builder frequency option">Monthly</T>
                      </SelectItem>
                      <SelectItem value="yearly">
                        <T context="cron builder frequency option">Yearly</T>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Every N minutes: Interval selector */}
                {frequency === 'every_n_minutes' && (
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">
                      <T context="cron builder run interval field">
                        Run Interval
                      </T>
                    </Label>
                    <Select
                      value={selectedMinuteInterval}
                      onValueChange={handleMinuteIntervalChange}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MINUTE_INTERVALS.map((interval) => (
                          <SelectItem
                            key={interval.value}
                            value={interval.value}
                          >
                            {interval.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Hourly: Minute offset selector */}
                {frequency === 'hourly' && (
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">
                      <T context="cron builder hourly minute offset field">
                        Run at minute
                      </T>
                    </Label>
                    <Select
                      value={parsed.minute}
                      onValueChange={handleHourlyMinuteChange}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MINUTES.map((m) => (
                          <SelectItem key={m.value} value={m.value}>
                            {gt(': {minute} (on the {when})', {
                              minute: m.label,
                              when:
                                m.value === '0'
                                  ? gt('hour', {
                                      context:
                                        'cron hourly minute option top of hour',
                                    })
                                  : m.label,
                              context: 'cron hourly minute select option',
                            })}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Time selector - only for daily/weekly/monthly/yearly */}
                {(frequency === 'daily' ||
                  frequency === 'weekly' ||
                  frequency === 'monthly' ||
                  frequency === 'yearly') && (
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground flex items-center gap-1">
                      <ClockIcon className="h-3 w-3" />
                      <T context="cron builder time of day field">Time</T>
                    </Label>
                    <div className="flex gap-2">
                      <Select
                        value={parsed.hour}
                        onValueChange={(h) =>
                          handleTimeChange(h, parsed.minute)
                        }
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue
                            placeholder={gt('Hour', {
                              context: 'cron time hour placeholder',
                            })}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {HOURS.map((h) => (
                            <SelectItem key={h.value} value={h.value}>
                              {h.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={
                          MINUTES.find((m) => m.value === parsed.minute)
                            ? parsed.minute
                            : '0'
                        }
                        onValueChange={(m) => handleTimeChange(parsed.hour, m)}
                      >
                        <SelectTrigger className="w-20">
                          <SelectValue
                            placeholder={gt('Min', {
                              context: 'cron time minute placeholder',
                            })}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {MINUTES.map((m) => (
                            <SelectItem key={m.value} value={m.value}>
                              :{m.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {/* Weekly: Day of week selector */}
                {frequency === 'weekly' && (
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">
                      <T context="cron builder days of week field">
                        Days of Week
                      </T>
                    </Label>
                    <div className="flex gap-1">
                      {DAYS_OF_WEEK.map((day) => (
                        <button
                          key={day.value}
                          type="button"
                          onClick={() => handleDayOfWeekToggle(day.value)}
                          className={cn(
                            'flex-1 py-1.5 text-xs font-medium rounded border transition-colors',
                            selectedDaysOfWeek.includes(day.value)
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'bg-background text-foreground border-input hover:bg-accent',
                          )}
                        >
                          {day.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Monthly: Day of month selector */}
                {frequency === 'monthly' && (
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">
                      <T context="cron builder day of month field">
                        Day of Month
                      </T>
                    </Label>
                    <Select
                      value={selectedDayOfMonth}
                      onValueChange={handleDayOfMonthChange}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DAYS_OF_MONTH.map((d) => (
                          <SelectItem key={d.value} value={d.value}>
                            {ordinal(parseInt(d.value))}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Yearly: Month and day selector */}
                {frequency === 'yearly' && (
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">
                      <T context="cron builder yearly date field">Date</T>
                    </Label>
                    <div className="flex gap-2">
                      <Select
                        value={selectedMonth}
                        onValueChange={handleMonthChange}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MONTHS.map((m) => (
                            <SelectItem key={m.value} value={m.value}>
                              {m.fullLabel}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={selectedDayOfMonth}
                        onValueChange={handleDayOfMonthChange}
                      >
                        <SelectTrigger className="w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DAYS_OF_MONTH.map((d) => (
                            <SelectItem key={d.value} value={d.value}>
                              {d.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Preview */}
            <div className="pt-2 border-t">
              <p className="text-xs text-muted-foreground">{description}</p>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {!validation.isValid && !isOpen && (
        <div className="flex items-center gap-1 text-xs text-destructive">
          <WarningCircleIcon className="h-3 w-3" />
          {validation.error}
        </div>
      )}
    </div>
  );
}

export default CronInput;
